import {
  useEffect, useMemo, useCallback, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, message } from 'antd';
import {
  useParams,
  useRouteMatch,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import validator from 'validator';
import { findRouteWithKey } from '@routes/routes';
import alarmsApi from '@api/alarmsApi';
import devicesApi from '@api/devicesApi';
import alarmsActions from '@state/alarms/actions';
import deviceActions from '@state/device/actions';
import variablesActions from '@state/variables/actions';

function useAlarmas_EditCreate() {

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();
  const location = useLocation();

  const { alarm_id } = useParams();
  const [form] = Form.useForm();

  const createEditValues = useSelector(({ alarms: a }) => a.createEditValues);
  const isEditDefault = useSelector(({ alarms: a }) => a.isEditDefault);

  const [container, setContainer] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isForCreate] = useState(!alarm_id);
  const [loadingAlarmToEdit, setLoadingAlarmToEdit] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const currentUserRol = useSelector(({ user }) => user.role);

  const skip = 0;
  const limit = 1000;

  const pathBefore = useMemo(() => {

    const { path: fromDefaults } = findRouteWithKey('RULES__DEFAULT');
    const { path: fromDynamic } = findRouteWithKey('RULES__DYNAMIC');

    const paths = [fromDefaults, fromDynamic];
    return paths.find((path) => match.path.includes(path));

  }, [match]);

  const breadcrumbData = useMemo(() => {

    const currentSection = isForCreate
      ? t('views.dynamic_alarms_create_edit.breadcrumb.create_alarm')
      : `${t('views.dynamic_alarms_create_edit.breadcrumb.edit_alarm')} / ${alarm_id}`;

    const { path: fromDefaults } = findRouteWithKey('RULES__DEFAULT');

    if (match.path.includes(fromDefaults)) {

      return [
        [fromDefaults, t('views.dynamic_alarms_create_edit.breadcrumb.default_rules')],
        [null, currentSection],
      ];

    }

    const { path: fromDynamic } = findRouteWithKey('RULES__DYNAMIC');
    return [
      [fromDynamic, t('views.dynamic_alarms_create_edit.breadcrumb.dynamic_rules')],
      [null, currentSection],
    ];

  }, [isForCreate, alarm_id, match, t]);

  const initialForm = useMemo(() => ({
    name: '',
    origin: '',
    description: null,
    severity: 'Critical',
    is_active: true,
    conditions: [],
    actions: [],
  }), []);

  const formOnValuesChange = useCallback(
    (changedValues) => {

      let newEditValues = { ...createEditValues };
      if (changedValues.origin) { // reset conditions if rule type changes

        newEditValues.conditions = [];

      }

      Object.entries(changedValues).forEach(([key, value]) => {

        newEditValues[key] = value;

      });

      if (newEditValues.origin !== 'Inst-Telemetry-Other') {

        newEditValues = { ...newEditValues, device_type: null };

      }

      dispatch(alarmsActions.setCreateEditValues(newEditValues));

    },
    [createEditValues, dispatch],
  );

  const isFirstStepValid = useCallback(async () => {

    try {

      await form.validateFields();

      if (createEditValues.origin === 'Inst-Telemetry-Other' && (createEditValues.device_type === '' || createEditValues.device_type === undefined)) {

        message.error(t('views.dynamic_alarms_create_edit.feedbacks.verify_devices_type'));
        return false;

      }

      return true;

    } catch {

      return false;

    }

  }, [form, createEditValues]);

  const isSecondStepValid = useCallback(() => {

    // Verificar que al menos haya un grupo
    const { conditions } = createEditValues || {};

    if (!conditions || conditions.length === 0) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.add_at_least_one_condition'));
      return false;

    }

    // Verificar que los grupos tengan al menos una condición
    const filtersEmptyGroup = conditions.filter((orConditions) => orConditions.length === 0);
    if (filtersEmptyGroup.length > 0) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.group_without_condition'));
      return false;

    }

    // Verificar que las orConditions este completas
    const allOrConditions = conditions.flat();
    let isValidAllOrConditions = true;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < allOrConditions.length; i++) {

      const orCondition = allOrConditions[i];
      const { params } = orCondition;

      const keysParams = Object.keys(params);

      if (keysParams.length === 0) {

        isValidAllOrConditions = false;
        break;

      }

      // Validators
      const isThreshold = ['operator', 'field', 'value'].every((key) => keysParams.includes(key));
      const isEquality = ['field', 'values'].every((key) => keysParams.includes(key));
      const isGeofence = ['operator', 'geofence_id'].every((key) => keysParams.includes(key));
      const isDeviceDisconnection = ['max_minutes'].every((key) => keysParams.includes(key));
      const isMissing = ['max_hours'].every((key) => keysParams.includes(key));

      isValidAllOrConditions = [
        isThreshold, isEquality, isGeofence, isDeviceDisconnection, isMissing,
      ].some((isGood) => isGood);

      if (isEquality) {

        const { values } = params;
        isValidAllOrConditions = Array.isArray(values) && values.length > 0;

      }

    }

    if (!isValidAllOrConditions) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.completed_conditions'));
      return false;

    }

    return true;

  }, [createEditValues, t]);

  const isStepValidate = useCallback(async () => {

    switch (currentStep) {

      case 0:
        // eslint-disable-next-line no-return-await
        return await isFirstStepValid();
      case 1:
        return isSecondStepValid();
      default:
        return false;

    }
    // eslint-disable-next-line
  }, [currentStep, createEditValues]);

  const onPrevious = useCallback(() => {

    setCurrentStep(currentStep - 1);

  }, [currentStep]);

  const onNext = useCallback(async () => {

    const isStepValid = await isStepValidate();
    if (isStepValid) setCurrentStep(currentStep + 1);
    // eslint-disable-next-line
  }, [currentStep, isStepValidate]);

  const onSave = useCallback(() => {

    const { actions } = createEditValues || {};

    const allUrls = [];
    const allEmails = [];

    actions.forEach((action) => {

      const { type, params } = action;
      switch (type) {

        case 'webhook':
          allUrls.push(...allUrls, ...params.urls);
          break;

        case 'report':
        case 'email':
          allEmails.push(...allEmails, ...params.recipients);
          break;

        default:
          break;

      }

    });

    // Check urls
    if (allUrls.some((url) => url.length === 0)) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.delete_empty_fields_url'));
      return;

    }

    // Check emails
    if (allEmails.some((email) => email.length === 0)) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.delete_empty_fields_email'));
      return;

    }
    // Check emails validate
    if (!allEmails.every((email) => validator.isEmail(email))) {

      message.error(t('views.dynamic_alarms_create_edit.feedbacks.error_fields_email'));
      return;

    }

    if (actions[0]?.params.time === '' && actions[0]?.type === 'report') {

      message.error(t('views.dynamic_alarms_create_edit.type_alarm_options.time_required'));
      return;

    }

    const { id } = createEditValues;
    const body = createEditValues;
    if (id) delete body.id;

    // setSaveLoading(true);
    setSaveLoading(false);

    const new_body = { ...body };
    const array = [];
    new_body.conditions.forEach((condition) => {

      const rules = [];
      condition.forEach((data) => {

        let value = {};
        if (new_body.origin === 'Device-Disconnection') { // in case it is Device-Disconnection

          value = {
            type: data.type,
            field: 'device',
            values: [new_body.gateway],
            max_minutes: data.params.max_minutes,
          };

        } else if (new_body.origin === 'Missing') { // in case it is Missing

          value = {
            type: data.type,
            field: 'device',
            values: [new_body.gateway],
            max_hours: data.params.max_hours,
          };

        } else { // for another case

          value = {
            type: data.type,
            field: data.params.field,
            value: data.params.value,
            gateway: new_body.gateway,
            operator: data.params.operator,
          };

        }
        rules.push(value);

      });
      array.push(rules);

    });
    const action_array = [];
    new_body.actions.forEach((data) => {

      const recipient = (data.params.recipients)
        ? data.params.recipients : [data.params.urls];
      const action = (data.type === 'report') ? {
          type: data.type,
          time: data.params.time,
          recipients: recipient,
        } : {
          type: data.type,
          recipients: recipient,
        };
      action_array.push(action);

    });
    new_body.conditions = array;
    new_body.actions = action_array;

    console.log(new_body);

    setSaveLoading(true);
    alarmsApi
      .createUpdateAlarm({ id, body: new_body })
      .then((response) => {

        console.log('createUpdateAlarm ', response);
        const arrayCondition = [];
        response.conditions.forEach((condition) => {

          const rules = [];
          let value = {};
          condition.forEach((data) => {

            if (response.origin === 'Device-Disconnection') { // in case it is Device-Disconnection

              value = {
                type: data.type,
                params: {
                  field: data.field,
                  value: [data.gateway],
                  max_minutes: data.max_minutes,
                },
              };

            } else if (response.origin === 'Missing') { // in case it is Missing

              value = {
                type: data.type,
                params: {
                  field: data.field,
                  value: [data.gateway],
                  max_hours: data.max_hours,
                },
              };

            } else { // for another case

              value = {
                type: data.type,
                params: {
                  field: data.field,
                  value: data.value,
                  gateway: data.gateway,
                  operator: data.operator,
                },
              };

            }
            rules.push(value);

          });
          arrayCondition.push(rules);

        });
        // eslint-disable-next-line no-shadow
        const action_array = [];
        response.actions.forEach((data) => {

          const action = {
            type: data.type,
            params: {
              recipients: [data.recipients],
            },
          };
          action_array.push(action);

        });
        response.conditions = arrayCondition;
        response.actions = action_array;

        setSaveLoading(false);

        if (isEditDefault) {

          dispatch(variablesActions.setDefaultAlarms(null));

        }

        if (!id) {

          dispatch(alarmsActions.addAlarmToListOrSumCount(response));
          history.replace(pathBefore);
          message.success(t('views.dynamic_alarms_create_edit.feedbacks.created_alarm'));
          return;

        }

        dispatch(alarmsActions.editAlarm(response));
        history.push(pathBefore);
        message.success(t('views.dynamic_alarms_create_edit.feedbacks.updated_alarm'));

      })
      .catch((resError) => {

        // eslint-disable-next-line no-console
        console.log('errr', resError);
        setSaveLoading(false);
        message.error(t('tables.logs.rows.error'));

      });

  }, [createEditValues, pathBefore, isEditDefault, dispatch, history, t]);

  const getAllDevice = useCallback( // get the Api key device data
    (businessTypeIds) => {

      let businessTypeIdsQuery = '';

      if (businessTypeIds !== '') {

          businessTypeIdsQuery += `business_type_id=${businessTypeIds}`;

}

      const abortC = new AbortController();
      const { signal } = abortC;
      devicesApi.getAllDeviceByBusiness2(signal, skip, limit, businessTypeIdsQuery)
        .then((response) => {

          dispatch(deviceActions.setSimpleAlarmsList(response));

        })
        .catch((error) => {

          // eslint-disable-next-line
          console.log('error: ', error);

        });

    },
    [dispatch],
  );

   // eslint-disable-next-line consistent-return
   useEffect(() => {

    dispatch(alarmsActions.setIsEditDefault({ forceIsEdit: false }));

    // use efect for resquest alarm to edit
    if (alarm_id) {

      const abortController = new AbortController();
      const { signal } = abortController;

      setLoadingAlarmToEdit(true);
      alarmsApi
        .getAlarmById({ id: alarm_id, signal })
        .then((response) => {

          const array = [];
          response.conditions.forEach((condition) => {

            const rules = [];
            condition.forEach((data) => {

              let value = {};

              if (response.origin === 'Device-Disconnection') { // in case it is Device-Disconnection

                value = {
                  type: data.type,
                  params: {
                    field: data.values[0],
                    value: data.values[0],
                    max_minutes: data.max_minutes,
                  },
                };

              } else if (response.origin === 'Missing') { // in case it is Missing

                value = {
                  type: data.type,
                  params: {
                    field: data.values[0],
                    value: data.values[0],
                    max_hours: data.max_hours,
                  },
                };

              } else { // for another case

                value = {
                  type: data.type,
                  params: {
                    field: data.field,
                    value: data.value,
                    operator: data.operator,
                  },
                };

              }

              rules.push(value);

            });
            array.push(rules);

          });
          const action_array = [];
          response.actions.forEach((data) => {

            let action = {};

            if (response.origin !== 'Inst-Telemetry-Other') {

               action = {
                type: data.type,
                params: {
                  recipients: data.recipients,
                },
              };

            } else {

               action = {
                type: data.type,
                params: {
                  time: data.time,
                  recipients: data.recipients,
                },
              };

          }

            action_array.push(action);

          });

          response.conditions = array;
          response.actions = action_array;

          getAllDevice(response?.business_type_id);

          dispatch(alarmsActions.setIsEditDefault(response));
          dispatch(alarmsActions.setCreateEditValues(response));
          setLoadingAlarmToEdit(false);

        })
        .catch((resError) => {

          if (signal.aborted) return;
          const { fem } = resError;
          // eslint-disable-next-line no-console
          console.log(t(fem));
          setLoadingAlarmToEdit(false);

        });

      // eslint-disable-next-line
      return () => {

        abortController.abort();

      };

    }

    dispatch(alarmsActions.setCreateEditValues(initialForm));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {

    if (!isEditDefault) {

      setCurrentStep(0);
      return;

}
    setCurrentStep(2);

  }, [isEditDefault]);

  // Limpia el CreateEditValues del redux al cerrar sección
  useEffect(() => () => dispatch(alarmsActions.setCreateEditValues(null)), [dispatch]);

  useEffect(() => { // mount component

    if (!isEditDefault && location.pathname === '/reglas/dinamicas/:alarm_id') {

      // If trying to edit without isEditDefault, redirect to tipo-negocio
        history.push('/reglas/dinamicas');

} else if (!isEditDefault && currentUserRol !== 'Superadmin' && currentUserRol !== 'Admin') {

          // eslint-disable-next-line
          // If trying to create without isEditDefault and not Superadmin or Admin, redirect to tipo-negocio
          history.push('/reglas/dinamicas');

}
    // eslint-disable-next-line
  }, []);

  return {
    breadcrumbData,
    container,
    setContainer,
    form,
    formOnValuesChange,
    createEditValues,
    loadingAlarmToEdit,
    currentStep,
    onPrevious,
    onNext,
    onSave,
    saveLoading,
    isEditDefault,
  };

}

export default useAlarmas_EditCreate;
